import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { withAuth } from '@cdk-prod/fortellis-auth-context';
import { PageFooter } from '@fortellis/page';
import { ApiPageTopSection } from './api-section/ApiPageTopSection';
import { MainSection } from 'cdk-radial';
import { APIDirectoryHome } from '@cdk-prod/api-directory-component';
import { fortellisTheme } from 'cdk-radial';
import { ThemeProvider } from 'styled-components';
import './api-directory.scss';
import {
  API_DIRECTORY_THEME,
  API_DIR_HOME_HANDLERS_MODEL,
  SEARCH_FILTER_URL_PARAMS
} from '../../../utils/constants';
import { APP_STATUS } from '../../../utils/constants';
import config from '../../../config/config';
import { BUILD_WITH_BUNDLES } from '../constants';
import axios from 'axios';
import { apiTypeSet } from './mocks/mockData';
import { useDirectoryApi, useGroupApi } from '../../../hooks/use-query';
import FortellisBot from '../../common/FortellisBot';
import { sendAmplitudeData } from '../../../utils/amplitude';
import { trackApiFilterChanged } from '../gtmTracking';

const envURLs = { DEVELOPER_UI_URL: `${config.developer.url}` };
const solutionUrl = config.api.solutionUrl;

const APIDirectory = props => {
  const {
    entityContext: { entity },
    auth
  } = props;
  const [filters, setFilters] = useState({
    selectedDomainIds: [],
    selectedAPIType: [],
    selectedFilterType: []
  });
  const [key, setKey] = useState(Math.random());
  const [appDataLoadStarted, setAppDataLoadStarted] = useState(false);
  const [appDataLoading, setAppDataLoading] = useState(true);
  const [appData, setAppData] = useState([]);
  const [apiData, setApiData] = useState({ data: [], loading: true });
  const [asyncApiData, setAsyncApiData] = useState({ data: [], loading: true });
  const [bundles, setBundles] = useState([]);
  const [groupData, setGroupData] = useState({ data: {}, loading: true });
  const isClearingAllRef = useRef(false);
  const location = useLocation();
  const history = useHistory();

  const {
    isAuthenticated,
    userData: { name: userName, email: userEmail } = {}
  } = auth;
  const { id: entityId, name: entityName } = entity || {};

  const {
    data: directoryData,
    isLoading: isDirectoryLoading,
    isFetching: isDirectoryFetching
  } = useDirectoryApi(props.auth);

  const authProp = {
    ...auth,
    isAuthenticated,
    userData: {
      name: userName,
      email: userEmail
    },
    orgData: {
      id: entityId,
      name: entityName
    }
  };

  if (!(isDirectoryLoading || isDirectoryFetching)) {
    if (apiData.loading && asyncApiData.loading) {
      const locBundles = directoryData?.bundles;
      const locRestApis = Array.isArray(directoryData?.items)
        ? directoryData.items.filter(f => f.kind === 'api')
        : [];
      const locAsyncApis = Array.isArray(directoryData?.items)
        ? directoryData.items.filter(f => f.kind === 'async-api')
        : [];
      setBundles(locBundles);
      setApiData({
        data: locRestApis,
        loading: false
      });

      setAsyncApiData({
        data: locAsyncApis,
        loading: false
      });
    }
  }

  const {
    data: domainData,
    isLoading: isDomainLoading,
    isFetching: isDomainFetching
  } = useGroupApi(props.auth);

  if (!(isDomainLoading || isDomainFetching)) {
    if (groupData.loading) {
      setGroupData({
        data: domainData,
        loading: false
      });
    }
  }

  const handleApiRedirection = ({ apiId, apiType }) => {
    const path =
      apiType === 'async-api' ? `/async-apis/${apiId}` : `/apis/${apiId}`;
    history.push(path);
  };

  const handleBundleDetailsRedirection = ({ bundleDetails }) => {
    const bundleId = bundleDetails?.id;
    if (!bundleId) return;
    history.push(`/bundles/${bundleId}`);
  };

  const handleBuildWithRedirection = ({ appId, buildWithType, bundleId }) => {
    if (buildWithType === BUILD_WITH_BUNDLES.NEW_APP) {
      window.open(
        `${config.developer.url}developer-account/solutions/register?bundleId=${bundleId}`
      );
    } else {
      window.open(
        `${config.developer.url}developer-account/solutions/edit/${appId}?bundleId=${bundleId}`
      );
    }
  };

  const getSelectedFilters = search => {
    const urlParams = new URLSearchParams(search);
    const selectedBundles =
      urlParams.get('Group')?.split(',').filter(Boolean) || [];
    const selectedTypes =
      urlParams.get('Type')?.split(',').filter(Boolean) || [];
    const selectedCategories =
      urlParams.get('Category')?.split(',').filter(Boolean) || [];
    return { selectedBundles, selectedTypes, selectedCategories };
  };

  const handleFiltersRedirection = payload => {
    if (!payload) {
      return;
    }
    const { searchKey, itemKey } = payload;

    // Handle "Clear All" action: { apiType: [], category: [], org: [], filterType: [] }
    if (!searchKey && !itemKey) {
      const oldParams = new URLSearchParams(window.location.search);
      const allOldValues = [
        ...(oldParams.get('Group')?.split(',').filter(Boolean) || []),
        ...(oldParams.get('Type')?.split(',').filter(Boolean) || []),
        ...(oldParams.get('Category')?.split(',').filter(Boolean) || [])
      ];

      if (allOldValues.length > 0) {
        trackApiFilterChanged({
          filterType: 'All',
          filterValue: allOldValues.join(', '),
          action: 'removed',
          activeGroup: [],
          activeType: [],
          activeCategories: [],
          apis: apiData?.data || [],
          asyncApis: asyncApiData?.data || [],
          bundles: bundles || []
        });
      }
      isClearingAllRef.current = true;
      return;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const queryParam = SEARCH_FILTER_URL_PARAMS[searchKey] || searchKey;
    let existingValues =
      urlParams.get(queryParam)?.split(',').filter(Boolean) || [];
    const isRemoving = existingValues.includes(itemKey);
    const action = isRemoving ? 'removed' : 'added';

    if (isRemoving) {
      existingValues = existingValues.filter(value => value !== itemKey);
    } else {
      existingValues.push(itemKey);
    }
    if (existingValues.length > 0) {
      urlParams.set(queryParam, existingValues.join(','));
    } else {
      urlParams.delete(queryParam);
    }

    const {
      selectedBundles,
      selectedTypes,
      selectedCategories
    } = getSelectedFilters(urlParams.toString());

    trackApiFilterChanged({
      filterType: queryParam,
      filterValue: itemKey,
      action,
      activeGroup: selectedBundles,
      activeType: selectedTypes,
      activeCategories: selectedCategories,
      apis: apiData?.data || [],
      asyncApis: asyncApiData?.data || [],
      bundles: bundles || []
    });

    sessionStorage.setItem('selectedFilters', urlParams.toString());
    const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
    window.history.pushState(null, '', newUrl);
  };

  const handleUrlparams = ({ newUrl }) => {
    if (isClearingAllRef.current) {
      isClearingAllRef.current = false;
      const updateUrl = newUrl.endsWith('?') ? newUrl.slice(0, -1) : newUrl;
      window.history.pushState(null, '', updateUrl);
      return;
    }

    if (newUrl) {
      const oldParams = new URLSearchParams(window.location.search);
      const newQueryString = newUrl.includes('?') ? newUrl.split('?')[1] : '';
      const newParams = new URLSearchParams(newQueryString);

      const filterKeys = ['Group', 'Type', 'Category'];
      let removedFilter = null;

      for (const key of filterKeys) {
        const oldVals =
          oldParams.get(key)?.split(',').filter(Boolean) || [];
        const newVals =
          newParams.get(key)?.split(',').filter(Boolean) || [];
        const removed = oldVals.filter(v => !newVals.includes(v));
        if (removed.length === 1 && oldVals.length === newVals.length + 1) {
          removedFilter = { filterType: key, filterValue: removed[0] };
          break;
        }
      }

      if (removedFilter) {
        const {
          selectedBundles,
          selectedTypes,
          selectedCategories
        } = getSelectedFilters(newParams.toString());
        trackApiFilterChanged({
          filterType: removedFilter.filterType,
          filterValue: removedFilter.filterValue,
          action: 'removed',
          activeGroup: selectedBundles,
          activeType: selectedTypes,
          activeCategories: selectedCategories,
          apis: apiData?.data || [],
          asyncApis: asyncApiData?.data || [],
          bundles: bundles || []
        });
      }
    }

    const updateUrl = newUrl.endsWith('?') ? newUrl.slice(0, -1) : newUrl;
    window.history.pushState(null, '', updateUrl);
  };

  const apiHomeOnClickHandlers = {
    ...API_DIR_HOME_HANDLERS_MODEL,
    handleApiRedirection: handleApiRedirection,
    viewBundleDetails: handleBundleDetailsRedirection,
    buildWithBundle: handleBuildWithRedirection,
    handleFiltersRedirection: handleFiltersRedirection,
    handleUrlparams: handleUrlparams
  };

  useEffect(() => {
    if (
      location?.state?.selectedAPIType &&
      location?.state?.selectedDomainIds
    ) {
      const filters = {
        selectedAPIType: location?.state?.selectedAPIType,
        selectedDomainIds: location?.state?.selectedDomainIds
      };
      setKey(Math.random());
      setFilters(filters);
    }

    let urlData = location?.search;
    let {
      selectedBundles,
      selectedTypes,
      selectedCategories
    } = getSelectedFilters(urlData);

    function getApiTypeNamesByIds(selectedTypes) {
      return apiTypeSet
        .filter(type => selectedTypes.includes(type.id))
        .map(type => type.name);
    }

    let apiTypeNames = getApiTypeNamesByIds(selectedTypes);

    if (location?.search) {
      const filters = {
        selectedAPIType: apiTypeNames,
        selectedDomainIds: selectedCategories,
        selectedFilterType: selectedBundles
      };
      setKey(Math.random());
      setFilters(filters);
    }
  }, [location?.state, location?.search]);

  useEffect(() => {
    if (auth?.accessToken && !appDataLoadStarted) {
      setAppDataLoadStarted(true);
      axios({
        method: 'GET',
        url: `${solutionUrl}/v1/solutions/?includeTest=false`,
        headers: {
          Authorization: `Bearer ${auth?.accessToken}`
        }
      })
        .then(response => {
          const appData = response?.data
            ?.filter(app => app?.status !== APP_STATUS.CANCELLED)
            .map(app => ({
              displayName: app?.displayName,
              id: app?.id
            }));
          setAppDataLoading(false);
          setAppData(appData);
        })
        .catch(error => {
          console.error('failed to get app details');
          setAppDataLoading(false);
        });
    } else if (auth?.loginPending === false && !auth?.isAuthenticated) {
      // we know for sure we're not logged in
      setAppDataLoading(false);
    }
  }, [
    auth?.accessToken,
    auth?.loginPending,
    auth?.isAuthenticated,
    appDataLoadStarted
  ]);

  return (
    <ThemeProvider theme={fortellisTheme}>
      <ApiPageTopSection entity={entity} />
      <MainSection className="main__section_page">
        <APIDirectoryHome
          auth={authProp}
          apis={apiData?.data || []}
          apisLoading={apiData?.loading}
          apiBundles={bundles || []}
          asyncApis={asyncApiData?.data || []}
          asyncApisLoading={asyncApiData?.loading}
          domains={groupData?.data || []}
          domainsLoading={groupData?.loading}
          cartDetails={[]}
          orderDetails={[]}
          apiHomeOnClickHandlers={apiHomeOnClickHandlers}
          theme={fortellisTheme}
          belongsTo={API_DIRECTORY_THEME.ISV_API_DIRECTORY}
          selectedAPIType={filters.selectedAPIType}
          selectedDomainIds={filters.selectedDomainIds}
          sendAmplitudeData={sendAmplitudeData}
          key={key}
          environmentURLs={envURLs}
          existingSolutionDetails={appData}
          disableBuildNow={appDataLoading}
          selectedFilterType={filters.selectedFilterType}
        />
        <FortellisBot auth={auth} entity={entity} />
      </MainSection>
      <PageFooter />
    </ThemeProvider>
  );
};

export default withAuth(APIDirectory);
